import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { send_component_save_request, send_request } from '../../utils/Request';
import { injectMetadataFields } from '../../utils/buildComponentSavePayload';
import { protectLoadedStore } from '../../utils/storeWriteGuard';
import { getCurrentTime } from '../../utils/getCurrentTime';
import useCodeStore, { getCodeStore } from './useCodeStore';
import useProjectStore from '../ProjectCanvas/useProjectStore';
import { IndividualComponentLoader } from '../../utils/ComponentLoadingSkeleton';
import Grid from '@mui/material/Grid';
import { addCompleter } from 'ace-builds/src-noconflict/ext-language_tools';
import 'ace-builds/src-noconflict/mode-javascript';
import 'ace-builds/src-noconflict/mode-python';
import 'ace-builds/src-noconflict/theme-github';
import 'ace-builds/src-noconflict/ext-language_tools';
import { JsDemoCode, PythonDemoCode } from './CodeComponent/AceEditorPlaceholder';
import InternetAccessSection from './CodeComponent/InternetAccessSection';
import SecretsSection from './CodeComponent/SecretsSection';
import CodeEditor from './CodeComponent/CodeEditor';
import PlaceholdersSection from './CodeComponent/PlaceholdersSection';
import { FREE_USER_TIERS, aceNameForLang, CODE_LANG } from './CodeUtil';
import CustomPaper from '../common/CustomPaper';

const CodeTemplate = (props) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const [availableSecrets, setAvailableSecrets] = useState([]);
  const [codePlaceholders, setCodePlaceholders] = useState([]);
  const [aceLangStyle, setAceLangStyle] = useState(CODE_LANG.PYTHON.aceName);
  const [changeDemo, setChangeDemo] = useState(true);
  const [description, setDescription] = useState(null);
  const [status, setStatus] = useState(null);
  const [updateState, setUpdateState] = useState(false);

  const store = useMemo(() => getCodeStore(props.component_id), [props.component_id]);

  const freeUserTier = useCallback(() => {
    const userTier = useProjectStore.getState().tier;
    return FREE_USER_TIERS.includes(userTier);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      if (props.component_id === undefined) return;
      setLoading(true);

      // Always fetch credentials (needed regardless of prefill)
      try {
        const response = await send_request(
          'ai-service/credential/user/types/',
          JSON.stringify(['VARIABLE', 'OAUTH2']),
          {},
          'POST'
        );
        let secrets = [];
        if (response?.data) {
          secrets = response.data.flatMap(
            credential => `\${${credential.data.placeholder}.SECRET}`
          );
        }
        setAvailableSecrets(secrets);
      } catch (err) {
        console.error('Error fetching credentials:', err);
      }

      // If store was prefilled from canvas load, skip the REST fetch
      if (store.getState().loaded) {
        console.info('[CODE-LOAD] Store already prefilled for', props.component_id);
        const storeData = store.getState().data;
        props.onComponentNameChanged(store.getState().name);
        props.onLastModifiedChanged(store.getState().lastModified);
        if (storeData) {
          if (storeData.placeholders) setCodePlaceholders(storeData.placeholders);
          if (storeData.language) {
            setAceLangStyle(aceNameForLang(storeData.language));
          }
        }

        setLoading(false);
        props.setChildTemplateLoaded(true);
        return;
      }

      // Fallback: fetch from REST
      console.info('[CODE-LOAD] REST fallback for', props.component_id);
      try {
        const response = await send_request(
          `project-service/project/component/query/${props.component_id}/at-version/${props.version}`,
          ''
        );

        if (response && response.data) {
          const { components } = response.data;
          if (components && components[0]) {
            const cd = components[0].componentData;
            console.info(
              '[CODE-LOAD] REST response',
              'componentId=', components[0].componentId,
              'schemaVersion=', cd?.schemaVersion,
              'codeLength=', (cd?.code || cd?.data?.code || '').length,
            );
            store.getState().setCodeData(components[0], props.version);
            store.setState({ loaded: true });
            protectLoadedStore(store);
            props.onComponentDescriptionChanged(components[0].description);
            props.onComponentNameChanged(store.getState().name);
            props.onLastModifiedChanged(store.getState().lastModified);

            setLoading(false);

            // Read placeholders and language from both v1 (nested under .data)
            // and v2 (at root of componentData) schema layouts.
            const dataSource = cd && cd.schemaVersion >= 2 ? cd : (cd && cd.data);
            if (dataSource) {
              if (dataSource.placeholders) {
                setCodePlaceholders(dataSource.placeholders);
              }
              if (dataSource.language) {
                setAceLangStyle(aceNameForLang(dataSource.language));
              }
            }
          }
        }

        if (!codePlaceholders || codePlaceholders.length === 0) {
          setCodePlaceholders([
            { key: 'output1', label: '', text: '', length: 1, style: {}, type: 'text' },
          ]);
        }

        props.setChildTemplateLoaded(true);

        setLoading(false);
      } catch (err) {
        setLoading(false);
      }
    };

    loadData();

    const placeholderParameters = [];
    const { availablePlaceholders } = props;

    if (availablePlaceholders?.length === 1) {
      const placeholders = availablePlaceholders[0]?.placeholders || [];
      placeholders.forEach(({ key }) => {
        const completeKey = `\${${key}}`;
        placeholderParameters.push({
          name: completeKey,
          value: completeKey,
          caption: completeKey,
          meta: 'local',
          score: 1000,
        });
      });
    }

    addCompleter({
      getCompletions: (editor, session, pos, prefix, callback) => {
        const currentSecrets = [...(availableSecrets || [])];
        const params = [...placeholderParameters];
        if (!freeUserTier() && store.getState().isInternetEnabledInStore()) {
          currentSecrets.forEach(secret => {
            params.push({
              name: secret,
              value: secret,
              caption: secret,
              meta: 'secret',
              score: 1000,
            });
          });
        }
        callback(null, params);
      },
    });

  }, []);

  const componentName = useProjectStore(s => s.componentName);
  useEffect(() => {
    store.getState().setCodeName(componentName);
  }, [componentName]);

  useEffect(() => {
    const unsub = useProjectStore.subscribe(
      s => s.selectedPlaceholder,
      (placeholder) => {
        const activeId = useProjectStore.getState().activeComponentId;
        if (activeId !== null && activeId !== props.component_id) return;
        if (!placeholder || !placeholder.selected) return;
        const { style, key } = placeholder.selected;
        if (
          props.availablePlaceholders &&
          props.availablePlaceholders.length === 1 &&
          props.availablePlaceholders[0].placeholders
        ) {
          for (let i = 0; i < props.availablePlaceholders[0].placeholders.length; i++) {
            if (key === props.availablePlaceholders[0].placeholders[i].key) {
              addPlaceholdersFn(
                '${' + key + '}',
                key,
                style,
                props.availablePlaceholders[0].placeholders[i]
              );
              addChip('${' + key + '}');
              return;
            }
          }
        }
      }
    );
    return unsub;
  }, []);

  const addPlaceholdersFn = useCallback((label, text, style, placeholder) => {
    const key = placeholder.key;
    const length = placeholder.length;
    const type = placeholder.type;
    store.getState().addPlaceholders({ label, text, style, key, length, type });
  }, []);

  const forceUpdateState = useCallback(() => {
    setUpdateState(prev => !prev);
  }, []);

  const handleInternetEnabledChange = useCallback((checked) => {
    store.getState().setCode(checked, 'internetEnabled');
    forceUpdateState();
  }, [forceUpdateState]);

  const handleCodeChange = useCallback((value, version) => {
    store.getState().setCode(value, 'code');
    store.getState().setCode(version, 'version');
    setChangeDemo(false);
  }, []);

  const handleLanguageChange = useCallback((codeLang) => {
    codeLangChange(codeLang);
  }, []);

  const handlePlaceholderChange = useCallback((value) => {
    store.getState().setCode(value, 'placeholders');
    setCodePlaceholders(prev => updateCodePlaceholdersByValue(value, prev));
  }, []);

  const handlePlaceholderDelete = useCallback((index) => {
    store.getState().deleteCodePlaceholder(index);
    setCodePlaceholders(prev => deleteStateFromIndex(index, prev));
  }, []);

  const handleAddPlaceholder = useCallback(() => {
    setCodePlaceholders(prev => {
      let indexNum = 0;
      if (prev) indexNum = prev.length - 1;

      const placeholderKey = { attribute: 'key', index: indexNum + 1, value: '' };
      store.getState().setCode(placeholderKey, 'placeholders');
      let updated = updateCodePlaceholdersByValue(placeholderKey, prev);

      const placeholderType = { attribute: 'type', index: indexNum + 1, value: 'text' };
      store.getState().setCode(placeholderType, 'placeholders');
      updated = updateCodePlaceholdersByValue(placeholderType, updated);

      return updated;
    });
  }, []);

  const codeLangChange = useCallback((value) => {
    if (value.includes(CODE_LANG.NODE.nameIncludes)) {
      if (changeDemo) {
        store.getState().setCode(JsDemoCode, 'code');
      }
    } else if (value.includes(CODE_LANG.PYTHON.nameIncludes)) {
      if (changeDemo) {
        store.getState().setCode(PythonDemoCode, 'code');
      }
    }
    setAceLangStyle(aceNameForLang(value));
    store.getState().setCode(value, 'language');
  }, [changeDemo]);

  const addChip = useCallback((chip) => {
    if (chip && chip.trim() !== '') {
      store.getState().setCode(chip, 'parameters');
      return true;
    }
    return false;
  }, []);

  if (!loading) {
    return (
      <div>
        <CustomPaper>
          <InternetAccessSection
            onInternetEnabledChange={handleInternetEnabledChange}
            isFreeUserTier={freeUserTier()}
            store={store}
          />

          <Grid container rowSpacing={1} alignItems="center">
            <Grid item xs={12}>
              {store.getState().isInternetEnabledInStore() && !freeUserTier() && (
                <SecretsSection
                  availableSecrets={availableSecrets}
                  isFreeUserTier={freeUserTier()}
                  store={store}
                />
              )}
            </Grid>
          </Grid>
        </CustomPaper>

        <CustomPaper style={{ marginTop: '16px' }}>
          <CodeEditor
            onCodeChange={handleCodeChange}
            onLanguageChange={handleLanguageChange}
            version={props.version}
            store={store}
          />
        </CustomPaper>

        <CustomPaper style={{ marginTop: '16px' }}>
          <PlaceholdersSection
            codePlaceholders={codePlaceholders}
            onPlaceholderChange={handlePlaceholderChange}
            onPlaceholderDelete={handlePlaceholderDelete}
            onAddPlaceholder={handleAddPlaceholder}
            store={store}
          />
        </CustomPaper>
      </div>
    );
  }
  return <IndividualComponentLoader />;
};

function updateCodePlaceholdersByValue(value, placeholders) {
  const result = [...placeholders];
  const index = value.index;
  if (index >= 0) {
    if (index < result.length) {
      switch (value.attribute) {
        case 'type':
          result[index] = { ...result[index], type: value.value };
          break;
        case 'key':
          result[index] = { ...result[index], key: value.value };
          break;
      }
    } else {
      const placeholder = {};
      switch (value.attribute) {
        case 'type':
          placeholder.type = value.value;
          break;
        case 'key':
          if (!placeholder.type) placeholder.type = 'text';
          placeholder.key = value.value;
          break;
      }
      result.push(placeholder);
    }
  }
  return result;
}

function deleteStateFromIndex(index, placeholders) {
  if (placeholders.length > index && index >= 0) {
    const newPlaceholders = [...placeholders];
    newPlaceholders.splice(index, 1);
    return newPlaceholders;
  }
  return placeholders;
}

export default CodeTemplate;
